import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import Section from '../components/Section';
import FeatureCard from '../components/FeatureCard';
import VideoFeed from '../components/VideoFeed';
import FeaturedNftGallery from '../components/FeaturedNftGallery';
import { Link } from 'react-router-dom';
import { SF_UI } from '../components/ui/tokens';
import { Network, Users, Brain, Globe, ArrowRight } from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: <Network size={48} />,
      title: 'Decentralized Platform',
      description: 'Built on Apertum blockchain for maximum security, transparency, and efficiency.'
    },
    {
      icon: <Users size={48} />,
      title: 'Community-Driven',
      description: 'Decisions made collectively by our global community of athletes and supporters.'
    },
    {
      icon: <Brain size={48} />,
      title: 'AI-Powered Support',
      description: 'Smart algorithms match athletes with the perfect scholarships and opportunities.'
    }
  ];

  return (
    <>
      <Hero
        title="Empowering Athletes Worldwide"
        subtitle="Join SportsFuture to unlock your potential"
        ctaText="Apply Now"
        ctaLink="/apply"
      />

      <Section
        eyebrow="Platform"
        title="Key Features"
        subtitle="SportsFuture leverages cutting-edge technology to create opportunities"
        centered={true}
        bgColor={SF_UI.sectionBand}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 0.1}
            />
          ))}
        </div>
      </Section>

      <Section
        bgColor="bg-gradient-to-br from-slate-800 to-blue-900"
        className="text-white"
      >
        <FeaturedNftGallery />
      </Section>

      <Section
        title="Latest Videos"
        subtitle="Stay updated with the latest content from SportsFuture"
        centered={true}
      >
        <VideoFeed />
      </Section>

      {/* Enhanced CTA Section */}
      <Section bgColor="bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
        <div className="text-center max-w-4xl mx-auto py-8">
          {/* Animated Globe Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-blue-500 shadow-lg shadow-green-500/25">
              <Globe className="w-10 h-10 text-white" />
            </div>
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-4 text-3xl font-bold text-white sm:text-4xl md:text-5xl"
          >
            Empowering Athletes{' '}
            <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              Worldwide
            </span>
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-300 mb-8"
          >
            Join SportsFuture to unlock your potential
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Link
              to="/apply"
              className="sf-ui-system sf-cta inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-secondary to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white font-semibold text-lg rounded-xl shadow-lg shadow-secondary/30 transition-all duration-300 hover:scale-105 hover:shadow-secondary/40"
            >
              Apply Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          {/* Bottom decorative text */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 text-sm text-gray-500"
          >
            Powered by Apertum Blockchain Technology
          </motion.p>
        </div>
      </Section>
    </>
  );
};

export default HomePage;
